import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface SavedRow {
  readonly savedSearchId: string;
  readonly savedSearchCode: string;
  readonly searchName: string;
  readonly queryText: string;
  readonly searchType: string;
  readonly scope: string;
  readonly scheduleFrequency: string;
  readonly isSubscribed: boolean;
  readonly status: string;
  readonly lastRunAt: string | null;
  readonly resultCountLast: number | null;
}

const SCOPES = ['PRIVATE', 'SHARED', 'DEPARTMENT', 'TENANT'];
const FREQUENCIES = ['NONE', 'DAILY', 'WEEKLY', 'MONTHLY'];
const PAGE = 25;

/**
 * M39 — Saved Search Manager (§28). A user's library of saved queries (T519):
 * create a named search (with an optional subscription schedule), then run it
 * on demand, share it with named recipients (flipping PRIVATE → SHARED), or
 * export its definition. Viewing / running needs SAVED_SEARCH_VIEW; creating /
 * sharing / exporting / deleting needs SAVED_SEARCH_MANAGE (SoD, §27). Ownership
 * is enforced server-side; a 403 surfaces the required permission.
 */
export function SavedSearchManager(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<SavedRow[]>([]);
  const [total, setTotal] = useState(0);
  const [scope, setScope] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [name, setName] = useState('');
  const [queryText, setQueryText] = useState('');
  const [newScope, setNewScope] = useState('PRIVATE');
  const [frequency, setFrequency] = useState('NONE');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: SavedRow[]; total: number }>(`/v1/search/saved?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, scope, page, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(t('m39.savedSearch.nameRequired')); return; }
    if (!queryText.trim()) { setActionError(t('m39.savedSearch.queryRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: SavedRow }>(`/v1/search/saved`, {
        method: 'POST',
        body: {
          searchName: name.trim(),
          queryText: queryText.trim(),
          scope: newScope,
          scheduleFrequency: frequency,
          isSubscribed: frequency !== 'NONE',
        },
        token,
      });
      setNotice(t('m39.savedSearch.saved', { code: r.data.savedSearchCode }));
      setName('');
      setQueryText('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m39.savedSearch.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const run = async (row: SavedRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/search/saved/${row.savedSearchId}/run`, { method: 'POST', token });
      setNotice(t('m39.savedSearch.ran', { code: row.savedSearchCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  const share = async (row: SavedRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/search/saved/${row.savedSearchId}/share`, {
        method: 'POST',
        body: { sharedWith: ['team'], scope: 'SHARED' },
        token,
      });
      setNotice(t('m39.savedSearch.shared', { code: row.savedSearchCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m39.savedSearch.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const exportOne = async (row: SavedRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      const r = await apiRequest<{ data: { savedSearchCode: string } }>(`/v1/search/saved/${row.savedSearchId}/export`, { method: 'POST', token });
      setNotice(t('m39.savedSearch.exported', { code: r.data.savedSearchCode }));
    } catch (err) {
      setActionError((err as Error).message);
    }
  };

  return (
    <main aria-label="saved-search-manager">
      <h1>{t('m39.savedSearch.title')}</h1>

      <section aria-label="create-saved">
        <h2>{t('m39.savedSearch.saveASearch')}</h2>
        <form aria-label="saved-form" onSubmit={(e) => void create(e)}>
          <label>{t('m39.common.name')}<input aria-label="field-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{t('m39.savedSearch.queryText')}<input aria-label="field-query" value={queryText} onChange={(e) => setQueryText(e.target.value)} /></label>
          <label>{t('m39.savedSearch.scope')}
            <select aria-label="field-scope" value={newScope} onChange={(e) => setNewScope(e.target.value)}>
              {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <label>{t('m39.savedSearch.schedule')}
            <select aria-label="field-frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-saved" disabled={busy}>{busy ? t('m39.common.saving') : t('m39.savedSearch.saveSearch')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="saved-error">{actionError}</p>}
        {notice && <p aria-label="saved-notice">{notice}</p>}
      </section>

      <section aria-label="saved-filters">
        <label>{t('m39.savedSearch.scope')}
          <select aria-label="filter-scope" value={scope} onChange={(e) => { setScope(e.target.value); setPage(0); }}>
            <option value="">{t('m39.savedSearch.allScopes')}</option>
            {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="saved-total">{t('m39.savedSearch.total', { total })}</p>
          <DataTable<SavedRow>
            ariaLabel="saved-table"
            testId="saved-table"
            rowKey={(r) => r.savedSearchId}
            rowDataAttrs={(r) => ({ 'data-saved': r.savedSearchCode })}
            emptyMessage={t('m39.savedSearch.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m39.common.code'), render: (r) => r.savedSearchCode },
              { key: 'name', header: t('m39.common.name'), render: (r) => r.searchName },
              { key: 'scope', header: t('m39.savedSearch.scope'), cellAriaLabel: (r) => `scope-${r.savedSearchCode}`, render: (r) => r.scope },
              { key: 'schedule', header: t('m39.savedSearch.schedule'), render: (r) => r.scheduleFrequency },
              { key: 'lastRun', header: t('m39.savedSearch.lastRun'), render: (r) => at(r.lastRunAt) },
              { key: 'actions', header: t('m39.common.actions'), render: (r) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`run-${r.savedSearchCode}`} onClick={() => void run(r)}>{t('m39.savedSearch.run')}</Button>
                  <Button variant="secondary" type="button" aria-label={`share-${r.savedSearchCode}`} onClick={() => void share(r)}>{t('m39.savedSearch.share')}</Button>
                  <Button variant="secondary" type="button" aria-label={`export-${r.savedSearchCode}`} onClick={() => void exportOne(r)}>{t('m39.savedSearch.export')}</Button>
                </>
              ) },
            ]}
          />

          <nav aria-label="saved-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m39.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m39.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m39.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
